import React, { useEffect } from "react";
import "./Cookiespolicy.css";
import { useLocation } from "react-router-dom";
import SEO from "./SEO";

const RefundPolicyPage = () => {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      document.querySelector(location.hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [location]);

  return (
    <>
      <SEO
        title="Refund & Cancellation Policy | Waggex HRMS"
        description="Read the Waggex Refund & Cancellation Policy to understand our subscription, billing, account cancellation, and refund terms."
        keywords="Waggex refund policy, cancellation policy, HRMS subscription, payroll software refund, billing policy"
        canonical="https://www.waggex.com/refund-and-cancellation"
      />

      <section id="CookiespolicyPage">
        <div className="container">
          <div className="cookies-policy-container">
            <h1 className="text-center">Refund &amp; Cancellation Policy</h1>

            <p>
              <strong>Effective Date:</strong> October 30, 2025
              <br />
              <strong>Last Updated:</strong> October 30, 2025
            </p>

            <h2>1. Introduction</h2>

            <p>
              This Refund &amp; Cancellation Policy explains the terms governing
              account cancellation, subscriptions, billing, and refunds for
              <strong> Waggex</strong>, a cloud-based Human Resource Management
              System (HRMS) that provides payroll, attendance, employee
              management, leave management, recruitment, and workforce
              management solutions.
            </p>

            <p>
              Waggex is owned and operated by
              <strong>
                {" "}
                Digital Engineering Excellence Programming ("DEEP")
              </strong>
              .
            </p>

            <p>
              Waggex allows businesses and organizations to create a free
              account. Every newly registered account receives
              <strong> Free 5 Employees</strong> at no additional cost. Certain
              premium features or subscription plans may require payment.
            </p>

            <p>
              By creating an account or purchasing any paid services, you agree
              to this Refund &amp; Cancellation Policy.
            </p>

            <p>
              Throughout this policy, <strong>"Waggex," "we," "our,"</strong>
              and <strong>"us"</strong> refer to
              <strong> Digital Engineering Excellence Programming</strong>, the
              owner and operator of the Waggex platform.
            </p>

            <h2>2. Free Account &amp; Subscription Plans</h2>

            <ul>
              <li>
                Businesses and organizations can register for a free Waggex
                account.
              </li>
              <li>
                Every new account includes
                <strong> 5 complimentary employees</strong> without any
                registration fee.
              </li>
              <li>
                Additional premium features, subscription plans, or enterprise
                services may be offered at separate pricing as displayed on our
                website or agreed upon with the customer.
              </li>
            </ul>

            <h2>3. Account Cancellation</h2>

            <p>
              You may close your Waggex account at any time by contacting our
              support team or through your account settings (where available).
            </p>

            <p>
              If you are subscribed to a paid plan, cancellation will stop
              future recurring billing after the current billing period. Your
              access to paid features will continue until the end of the active
              subscription period unless otherwise specified.
            </p>

            <p>
              Cancellation of your account does
              <strong> not automatically entitle you to a refund.</strong>
            </p>

            <h2>4. Refund Policy</h2>

            <p>
              Creating a free Waggex account and using the
              <strong> 5 complimentary employees</strong> does not require any
              payment and therefore is not eligible for a refund.
            </p>

            <p>
              For paid subscriptions or premium services, all fees are generally
              <strong> non-refundable</strong>, except where required under
              applicable law or approved by Waggex in exceptional circumstances.
            </p>

            <p>Refunds will generally not be provided for:</p>

            <ul>
              <li>Partial use of paid services.</li>
              <li>Unused subscription periods.</li>
              <li>Change of mind after purchase.</li>
              <li>Failure to cancel before the next billing cycle.</li>
              <li>Incorrect plan selection by the customer.</li>
              <li>Failure to use the purchased services.</li>
            </ul>

            <h2>5. Exceptions</h2>

            <p>
              Refund requests may be considered only in the following
              situations:
            </p>

            <ul>
              <li>Duplicate payment for the same subscription.</li>
              <li>
                Incorrect billing due to a verified technical error by Waggex.
              </li>
              <li>
                Payment successfully completed but the subscribed service was
                not activated due to a verified technical issue.
              </li>
            </ul>

            <p>
              If approved, refunds will be processed through the original
              payment method within
              <strong> 7–10 business days</strong>, subject to the processing
              timelines of the respective payment provider.
            </p>

            <h2>6. Service Suspension</h2>

            <p>
              Waggex reserves the right to suspend or terminate access to the
              Services if:
            </p>

            <ul>
              <li>Subscription fees remain unpaid.</li>
              <li>The account is used in violation of our Terms of Service.</li>
              <li>Fraudulent, unlawful, or abusive activities are detected.</li>
              <li>
                Incorrect or misleading information is provided during
                registration or use of the Services.
              </li>
            </ul>

            <p>
              Suspension or termination of services due to such violations shall
              <strong> not entitle the customer to any refund.</strong>
            </p>

            <h2>7. Changes to This Policy</h2>

            <p>
              We may update this Refund &amp; Cancellation Policy from time to
              time to reflect changes in our services, business practices, or
              legal requirements.
            </p>

            <p>
              Any updates will be published on this page along with the revised
              <strong> Last Updated</strong> date.
            </p>

            <p>
              Your continued use of the Services after such updates constitutes
              your acceptance of the revised policy.
            </p>

            <h2>8. Contact Us</h2>
            <p>
              If you have any questions regarding these Terms, please contact
              us:
            </p>
            <p>
              <strong>
                {" "}
                Digital Engineering Excellence Programming (DEEP)
              </strong>{" "}
            </p>
            <p>
              <strong> Operating Brand: Waggex</strong>
            </p>
            <p>
              <strong>Address:</strong> Phase 8-B, Industrial Area, Sector 74,
              Sahibzada Ajit Singh Nagar, Punjab – 140307, India
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:support@waggex.com" rel="canonical">
                support@waggex.com
              </a>
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default RefundPolicyPage;
